<template>
  <div class="ec-form-group">
    <label v-if="label" class="ec-form-label">
      {{ label }}
      <span v-if="required" class="ec-form-label__required">*</span>
    </label>
    <div
      ref="wrapEl"
      class="ec-input ec-combo-select"
      :class="{ 'ec-input--error': error, 'ec-input--disabled': disabled }"
    >
      <input
        ref="inputEl"
        class="ec-input__field"
        type="text"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="searchTerm"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <button
        type="button"
        class="ec-combo-select__chevron"
        tabindex="-1"
        :disabled="disabled"
        @mousedown.prevent="toggleDropdown"
      >
        <i class="bi bi-chevron-down" />
      </button>

      <div v-if="isOpen && !disabled" class="ec-combo-select__content" :style="dropdownStyle">
        <div v-if="!filteredOptions.length && !isNewValue" class="ec-combo-select__empty">
          Aucun résultat
        </div>

        <div
          v-for="opt in filteredOptions"
          :key="opt.value"
          class="ec-combo-select__item"
          :class="{ 'ec-combo-select__item--selected': opt.value === value }"
          @mousedown.prevent="onOptionClick(opt.value)"
        >
          {{ opt.label }}
        </div>

        <div
          v-if="isNewValue"
          class="ec-combo-select__item ec-combo-select__item--create"
          @mousedown.prevent="onOptionClick(searchTerm.trim())"
        >
          <i class="bi bi-plus-lg" />
          {{ searchTerm.trim() }}
        </div>
      </div>
    </div>
    <div v-if="error" class="ec-form-error">{{ error }}</div>
    <small v-if="hint && !error" class="ec-form-hint">{{ hint }}</small>
  </div>
</template>

<script>
export default {
  name: 'EcComboSelect',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
    hint: { type: String, default: '' },
    required: { type: Boolean, default: false },
    allowCreate: { type: Boolean, default: false },
  },

  data() {
    return {
      isOpen: false,
      searchTerm: '',
      isTyping: false,
      dropdownStyle: {},
    }
  },

  computed: {
    filteredOptions() {
      if (!this.isTyping) return this.options
      var term = this.searchTerm.toLowerCase()
      if (!term) return this.options
      return this.options.filter(function (o) {
        return o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term)
      })
    },

    isNewValue() {
      if (!this.allowCreate || !this.searchTerm.trim()) return false
      var term = this.searchTerm.trim().toLowerCase()
      return !this.options.some(function (o) {
        return o.value.toLowerCase() === term || o.label.toLowerCase() === term
      })
    },
  },

  watch: {
    value() {
      if (!this.isTyping) this.syncInputDisplay()
    },
    options() {
      if (!this.isTyping) this.syncInputDisplay()
    },
  },

  mounted() {
    this.syncInputDisplay()
  },

  methods: {
    getLabel(val) {
      if (!val) return ''
      var opt = this.options.find(function (o) { return o.value === val })
      return opt ? opt.label : val
    },

    computeDropdownPosition() {
      var el = this.$refs.wrapEl
      if (!el) return
      var rect = el.getBoundingClientRect()
      this.dropdownStyle = {
        position: 'fixed',
        top: (rect.bottom + 4) + 'px',
        left: rect.left + 'px',
        width: rect.width + 'px',
        zIndex: 9999,
      }
    },

    toggleDropdown() {
      if (this.isOpen) {
        this.isOpen = false
      } else {
        this.computeDropdownPosition()
        this.isOpen = true
      }
    },

    syncInputDisplay() {
      var label = this.getLabel(this.value)
      this.searchTerm = label
      this.$nextTick(function () {
        if (this.$refs.inputEl) this.$refs.inputEl.value = label
      }.bind(this))
    },

    onOptionClick(optValue) {
      this.isTyping = false
      this.isOpen = false
      this.$emit('input', optValue)
      this.$nextTick(function () { this.syncInputDisplay() }.bind(this))
    },

    onInput(event) {
      this.isTyping = true
      this.searchTerm = event.target.value
      if (!this.isOpen) this.isOpen = true
    },

    onFocus() {
      this.computeDropdownPosition()
      this.isOpen = true
    },

    onBlur() {
      var self = this
      setTimeout(function () {
        self.isOpen = false
        self.isTyping = false

        var term = self.searchTerm.trim()
        if (!term) {
          self.syncInputDisplay()
          return
        }

        var match = self.options.find(function (o) {
          return o.label.toLowerCase() === term.toLowerCase() ||
                 o.value.toLowerCase() === term.toLowerCase()
        })

        if (match) {
          if (match.value !== self.value) self.$emit('input', match.value)
        } else if (self.allowCreate) {
          if (term !== self.value) self.$emit('input', term)
        }

        self.$nextTick(function () { self.syncInputDisplay() })
      }, 200)
    },
  },
}
</script>

<style>
.ec-combo-select {
  position: relative;
}

.ec-combo-select__chevron {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  font-size: 0.75rem;
  color: var(--ec-text-muted, #6b7280);
  background: none;
  border: none;
  cursor: pointer;
}

.ec-combo-select__content {
  max-height: 220px;
  overflow-y: auto;
  background: var(--ec-bg, #fff);
  border: 1px solid var(--ec-border-color, #e2e8f0);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 0.25rem;
  margin-top: 4px;
}

.ec-combo-select__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 6px;
  cursor: pointer;
  color: var(--ec-text-color, #1f2937);
  transition: background 0.1s ease;
}

.ec-combo-select__item:hover {
  background: var(--ec-bg-muted, #f3f4f6);
}

.ec-combo-select__item--selected {
  font-weight: 600;
  color: var(--ec-primary, #3b82f6);
}

.ec-combo-select__item--create {
  color: var(--ec-primary, #3b82f6);
  font-style: italic;
}

.ec-combo-select__empty {
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  color: var(--ec-text-muted, #6b7280);
}
</style>
